import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Code2, FileText, Globe, Palette, ScrollText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  getRelevanceBadgeLabel,
  resolveRelevanceLabel,
  type GroupedResult,
  type RelevanceBadgeLabels,
  type SearchResult,
} from './searchResults';
import { cn } from '@/lib/utils';
import { searchResultTitle } from './searchResultTitle';

const ICONS = {
  web: Globe,
  code: Code2,
  design: Palette,
  article: ScrollText,
  document: FileText,
} as const;

const RELEVANCE_VARIANT = {
  high: 'border-transparent bg-success text-success-foreground',
  medium: 'border-transparent bg-warning text-warning-foreground',
  low: 'bg-muted text-foreground',
} as const;

interface SearchResultRowProps {
  group: GroupedResult;
  labels: RelevanceBadgeLabels;
  citationLabel: (result: SearchResult) => string;
  isSelected: boolean;
  isInContext: boolean;
  showsSegments: boolean;
  onToggleSelected: () => void;
  onToggleSegments: () => void;
  onResultClick?: (result: SearchResult) => void;
}

const SearchResultRow: React.FC<SearchResultRowProps> = ({
  group,
  labels,
  citationLabel,
  isSelected,
  isInContext,
  showsSegments,
  onToggleSelected,
  onToggleSegments,
  onResultClick,
}) => {
  const { t } = useTranslation();
  const { primary, additional } = group;
  const primaryTitle = searchResultTitle(t, primary);
  const Icon = ICONS[primary.type] ?? FileText;

  // The file's best segment, not the primary's — the primary is chosen for
  // having text so click-through can highlight, and may score lower (#783).
  const best = [primary, ...additional].reduce((a, b) =>
    (b.relevance ?? 0) > (a.relevance ?? 0) ? b : a,
  );
  const relevance = resolveRelevanceLabel(best.relevance, best.relevanceLabel);
  const relevanceText = getRelevanceBadgeLabel(best.relevance, best.relevanceLabel, labels);

  return (
    <li className="rounded-md px-1 py-1.5 transition-colors hover:bg-accent/50">
      <div className="flex items-start gap-2">
        <span className="mt-0.5 shrink-0 rounded-sm bg-muted px-1.5 py-0.5 text-[11px] font-medium revy-figure text-muted-foreground">
          {citationLabel(primary)}
        </span>

        <Checkbox
          checked={isSelected || isInContext}
          disabled={isInContext}
          onCheckedChange={onToggleSelected}
          aria-label={primaryTitle}
          className="mt-1 shrink-0"
        />

        <Icon aria-hidden="true" className="mt-1 size-4 shrink-0 text-muted-foreground" strokeWidth={1.8} />

        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => onResultClick?.(primary)}
            className="w-full text-left focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
          >
            {/* Its own line: sharing with the chips starved a file name to
                161px in a 460px panel. File names have no spaces to break on,
                so it clamps to two lines rather than ellipsising to nothing. */}
            <span
              data-slot="result-name"
              title={primaryTitle}
              className="line-clamp-2 block text-sm font-medium [overflow-wrap:anywhere]"
            >
              {primaryTitle}
            </span>

            {primary.snippet && (
              <span className="mt-0.5 line-clamp-2 block text-xs leading-relaxed text-muted-foreground">
                {primary.snippet}
              </span>
            )}
          </button>

          {primary.aggregation && (
            <p className="mt-1 text-xs text-muted-foreground" data-slot="aggregation-status">
              {primary.aggregation.count_status === 'exact' && primary.aggregation.total_count !== null
                ? t('chat.aggregation.indexedCount', {
                    count: primary.aggregation.total_count,
                    grain: t(primary.aggregation.aggregation_grain === 'distinct_literal_value'
                      ? 'chat.aggregation.literalValues' : 'chat.aggregation.physicalRows'),
                  })
                : t('chat.aggregation.unknownTotal')}
              {' '}{t('chat.aggregation.sourceUnknown')}
              {primary.coverageStatus !== 'complete' && (
                <> {t('chat.aggregation.partialEvidence')}</>
              )}
            </p>
          )}

          <div className="mt-1.5 flex flex-wrap items-center gap-1">
            {primary.sheetName && (
              <Badge variant="outline" className="h-5 max-w-[180px] truncate px-1.5 text-[10px]">
                {`${primary.sheetName} · ${primary.cellRange || primary.cellRef || '-'}`}
              </Badge>
            )}
            {relevanceText && relevance && (
              <Badge variant="outline" className={cn('h-5 px-1.5 text-[10px]', RELEVANCE_VARIANT[relevance])}>
                {relevanceText}
              </Badge>
            )}
            {additional.length > 0 && (
              <Badge variant="outline" className="h-5 px-1.5 text-[10px] text-primary">
                {t('chat.relatedPassages', { count: additional.length + 1 })}
              </Badge>
            )}
            {isInContext && (
              <Badge variant="outline" className="h-5 gap-1 px-1.5 text-[10px] text-success">
                <Check className="size-3" strokeWidth={2.5} />
                {t('chat.inContext')}
              </Badge>
            )}
          </div>

          {additional.length > 0 && (
            <button
              type="button"
              onClick={onToggleSegments}
              className="mt-1.5 flex items-center gap-1 text-xs text-primary underline-offset-2 hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronDown
                aria-hidden="true"
                className={cn('size-3.5 transition-transform', showsSegments && 'rotate-180')}
                strokeWidth={2}
              />
              {showsSegments
                ? t('chat.hideRelatedPassages')
                : t('chat.showRelatedPassages', {
                    count: additional.length,
                    // The unit is pluralised by hand; en has no _one/_other keys.
                    unit:
                      additional.length === 1
                        ? t('chat.relatedPassage')
                        : t('chat.relatedPassagesUnit'),
                  })}
            </button>
          )}

          {showsSegments && (
            <ul className="mt-1.5 space-y-1 border-l pl-3">
              {additional.map((segment) => (
                <li key={segment.segmentId ?? `${segment.citationIndex}-${segment.snippet}`}>
                  <button
                    type="button"
                    onClick={() => onResultClick?.(segment)}
                    className="flex w-full items-start gap-2 rounded-sm px-1 py-1 text-left hover:bg-accent/50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="shrink-0 rounded-sm bg-muted px-1 text-[10px] revy-figure text-muted-foreground">
                      {citationLabel(segment)}
                    </span>
                    <span className="line-clamp-2 text-xs text-muted-foreground">
                      {segment.snippet || segment.excerpt || t('chat.noPreview')}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </li>
  );
};

export default SearchResultRow;
